import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

import { useUpdateMyConfig } from '../redux/api/users/usersApi';
import {
  PrimaryColor,
  ThemeMode,
  UserConfigKey,
  defaultPrimaryColorKey,
} from '../shared/constants';
import {
  LocalStorageKey,
  LocalStorageService,
} from '../shared/storage/local-storage';
import { darkPalette } from '../styles/palette-dark';
import { lightPalette } from '../styles/palette-light';
import { mergePrimaryIntoPalette } from '../styles/palette-with-primary';
import { createAppTheme } from '../styles/theme';
import { useUserConfig } from './UserConfigContext';

const VALID_THEMES: string[] = [ThemeMode.light, ThemeMode.dark];
const VALID_PRIMARY_COLORS: string[] = Object.values(PrimaryColor);

export const BODY_DARK_THEME_CLASS = 'dark-theme';

function getInitialTheme(): ThemeMode {
  const stored = LocalStorageService.getValue(LocalStorageKey.theme);
  if (stored && VALID_THEMES.includes(stored)) {
    return stored as ThemeMode;
  }
  return ThemeMode.light;
}

function getInitialPrimaryColor(): PrimaryColor {
  const stored = LocalStorageService.getValue(LocalStorageKey.primaryColor);
  if (stored && VALID_PRIMARY_COLORS.includes(stored)) {
    return stored as PrimaryColor;
  }
  return defaultPrimaryColorKey;
}

export interface IVisualizationConfig {
  theme: ThemeMode;
  primaryColor: PrimaryColor;
}

interface IThemeContextValue {
  theme: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
  primaryColor: PrimaryColor;
  setPrimaryColor: (color: PrimaryColor) => void;
  updateVisualizationConfig: (config: IVisualizationConfig) => void;
  applyVisualizationConfig: (config: IVisualizationConfig) => void;
}

const ThemeContext = React.createContext<IThemeContextValue | undefined>(
  undefined,
);

interface IThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider(props: IThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeMode>(getInitialTheme);
  const [primaryColor, setPrimaryColorState] = useState<PrimaryColor>(
    getInitialPrimaryColor,
  );
  const { options } = useUserConfig();
  const [updateMyConfig] = useUpdateMyConfig();
  const pendingThemeRef = useRef<ThemeMode | null>(null);

  const setTheme = useCallback(
    (mode: ThemeMode) => {
      pendingThemeRef.current = mode;
      setThemeState(mode);
      LocalStorageService.setValue(LocalStorageKey.theme, mode);
      updateMyConfig({
        config: [{ key: UserConfigKey.theme, value: mode }],
      });
    },
    [updateMyConfig],
  );

  const setPrimaryColor = useCallback((color: PrimaryColor) => {
    setPrimaryColorState(color);
    LocalStorageService.setValue(LocalStorageKey.primaryColor, color);
  }, []);

  const applyVisualizationConfig = useCallback(
    (config: IVisualizationConfig) => {
      setThemeState(config.theme);
      setPrimaryColorState(config.primaryColor);
      LocalStorageService.setValue(LocalStorageKey.theme, config.theme);
      LocalStorageService.setValue(
        LocalStorageKey.primaryColor,
        config.primaryColor,
      );
    },
    [],
  );

  const updateVisualizationConfig = useCallback(
    (config: IVisualizationConfig) => {
      applyVisualizationConfig(config);
      updateMyConfig({
        config: [
          { key: UserConfigKey.theme, value: config.theme },
          { key: UserConfigKey.primaryColor, value: config.primaryColor },
        ],
      });
    },
    [updateMyConfig, applyVisualizationConfig],
  );

  useEffect(() => {
    const configTheme = options.theme;
    if (configTheme && VALID_THEMES.includes(configTheme)) {
      if (pendingThemeRef.current !== null) {
        if (configTheme === pendingThemeRef.current) {
          pendingThemeRef.current = null;
        }
      } else if (configTheme !== theme) {
        setThemeState(configTheme);
        LocalStorageService.setValue(LocalStorageKey.theme, configTheme);
      }
    }

    const configPrimaryColor = options.primaryColor;
    if (
      configPrimaryColor &&
      VALID_PRIMARY_COLORS.includes(configPrimaryColor) &&
      configPrimaryColor !== primaryColor
    ) {
      setPrimaryColorState(configPrimaryColor as PrimaryColor);
      LocalStorageService.setValue(
        LocalStorageKey.primaryColor,
        configPrimaryColor as PrimaryColor,
      );
    }
  }, [options, theme, primaryColor]);

  useLayoutEffect(() => {
    if (theme === ThemeMode.dark) {
      document.body.classList.add(BODY_DARK_THEME_CLASS);
    } else {
      document.body.classList.remove(BODY_DARK_THEME_CLASS);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        primaryColor,
        setPrimaryColor,
        updateVisualizationConfig,
        applyVisualizationConfig,
      }}>
      <MuiThemeFromMode>{props.children}</MuiThemeFromMode>
    </ThemeContext.Provider>
  );
}

export function useThemeContext(): IThemeContextValue {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within ThemeProvider');
  }
  return context;
}

export function MuiThemeFromMode(props: { children: React.ReactNode }) {
  const { theme: mode, primaryColor } = useThemeContext();
  const muiTheme = useMemo(() => {
    const basePalette = mode === ThemeMode.dark ? darkPalette : lightPalette;
    const palette = mergePrimaryIntoPalette(
      basePalette as Record<string, unknown>,
      primaryColor,
      mode === ThemeMode.dark,
    );
    return createAppTheme(palette);
  }, [mode, primaryColor]);
  return <MuiThemeProvider theme={muiTheme}>{props.children}</MuiThemeProvider>;
}
